/**
 * Settings → System → AI provider (ADR-004 §7.1): the six `llmSettings.*` routes, all `settings.system`.
 * Every shape comes from @amplispot/contracts (llm.ts); nothing here redeclares one.
 *
 * A test is asynchronous: POST /settings/llm/test answers 202 with the checks `running`, the worker runs the call,
 * and GET /settings/llm shows the result — so the settings query polls while any check is still running.
 */
import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  type ClaudeLoginStatus,
  type CompleteClaudeLoginBody,
  LLM_PROVIDER_LABEL,
  LLM_PURPOSE_LABEL,
  type KeyedLlmProvider,
  type LlmProviderId,
  type LlmSettingsResponse,
  type LlmUsageQuery,
  type PatchLlmSettingsBody,
  type TestLlmBody,
} from '@amplispot/contracts';
import { call, newIdempotencyKey } from '../api';

export const llmKeys = {
  all: ['llm'] as const,
  settings: ['llm', 'settings'] as const,
  claudeLogin: ['llm', 'claude-login'] as const,
  usage: (q: LlmUsageQuery) => ['llm', 'usage', q.from ?? '', q.to ?? ''] as const,
};

/** True while the worker has not answered a test yet. */
export const anyRunning = (s: LlmSettingsResponse | undefined) => !!s?.providers.some((p) => p.last_check?.status === 'running');

export function useLlmSettings(enabled = true) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: llmKeys.settings,
    queryFn: () => call('llmSettings.get'),
    enabled,
    refetchInterval: (q) => (anyRunning(q.state.data) ? 2500 : false),
  });
  // A finished test is a recorded call: refresh usage when the last running check answers.
  const was = useRef(false);
  const running = anyRunning(q.data);
  useEffect(() => {
    if (was.current && !running) void qc.invalidateQueries({ queryKey: ['llm', 'usage'] });
    was.current = running;
  }, [running, qc]);
  return q;
}

export const useLlmUsage = (q: LlmUsageQuery, enabled = true) =>
  useQuery({ queryKey: llmKeys.usage(q), queryFn: () => call('llmSettings.usage', { query: q }), enabled });

/** A key change or a test changes the provider's readiness, and a test call is itself recorded in usage. */
const refreshAll = (qc: QueryClient) => qc.invalidateQueries({ queryKey: llmKeys.all });

export function useUpdateLlm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { version: number; body: PatchLlmSettingsBody }) => call('llmSettings.update', { body: v.body, ifMatch: v.version }),
    onSuccess: (r) => qc.setQueryData(llmKeys.settings, r),
  });
}

export function usePutLlmKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { provider: KeyedLlmProvider; api_key: string }) =>
      call('llmSettings.putSecret', { params: { provider: v.provider }, body: { api_key: v.api_key } }),
    onSettled: () => refreshAll(qc),
  });
}

export function useDeleteLlmKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (provider: KeyedLlmProvider) => call('llmSettings.deleteSecret', { params: { provider } }),
    onSettled: () => refreshAll(qc),
  });
}

export function useTestLlm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TestLlmBody) => call('llmSettings.test', { body }),
    onSuccess: (r) => {
      qc.setQueryData(llmKeys.settings, r);
      void qc.invalidateQueries({ queryKey: ['llm', 'usage'] });
    },
  });
}

// ---------------------------------------------------------------- Claude CLI sign-in (claudeLogin.*)

/**
 * GET /settings/llm/claude-login. An open sign-in closes by itself at `pending.expires_at`, so the status is read again
 * then (the server drops `pending` once it has passed).
 */
export function useClaudeLogin(enabled = true) {
  const q = useQuery({ queryKey: llmKeys.claudeLogin, queryFn: () => call('claudeLogin.get'), enabled });
  const closesAt = q.data?.pending?.expires_at;
  const { refetch } = q;
  useEffect(() => {
    if (!closesAt) return;
    const ms = Math.max(0, new Date(closesAt).getTime() - Date.now()) + 1000;
    const t = setTimeout(() => void refetch(), Math.min(ms, 2 ** 31 - 1));
    return () => clearTimeout(t);
  }, [closesAt, refetch]);
  return q;
}

/** Start answers the open sign-in only; read the whole status again so a reload and this page agree. */
export function useStartClaudeLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => call('claudeLogin.start', { idempotencyKey: newIdempotencyKey() }),
    onSettled: () => qc.invalidateQueries({ queryKey: llmKeys.claudeLogin }),
  });
}

/**
 * Complete answers the new status. A new token makes the Claude CLI untested again, so the provider cards are read
 * again too. A refusal leaves the sign-in open (or closed, when it was no longer open): read the status either way.
 */
export function useCompleteClaudeLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CompleteClaudeLoginBody) => call('claudeLogin.complete', { body }),
    onSuccess: (r) => qc.setQueryData(llmKeys.claudeLogin, r),
    onSettled: () => refreshAll(qc),
  });
}

export function useClaudeLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => call('claudeLogin.delete'),
    onSuccess: (r) => qc.setQueryData(llmKeys.claudeLogin, r),
    onSettled: () => refreshAll(qc),
  });
}

/** "17 Oct 2027" in IST — a sign-in lasts a year, so the year matters. */
export const fullDayLabel = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });

/** "4:05 pm" in IST — when an open sign-in closes. */
export const timeLabel = (iso: string) =>
  new Date(iso).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' }).toLowerCase();

/** A stored sign-in this close to its end is flagged so it is renewed before the CLI stops working. */
export const RENEW_WARNING_DAYS = 30;

/** Whole days until `iso` (0 on the day itself, negative after). */
export const daysUntil = (iso: string, now = Date.now()) => Math.floor((new Date(iso).getTime() - now) / 86_400_000);

/** The chip on the Claude CLI sign-in block: [chip class, label]. */
export function claudeLoginChip(s: ClaudeLoginStatus, now = Date.now()): [string, string] {
  switch (s.state) {
    case 'pending':
      return ['info', s.last4 ? 'Renewing' : 'Signing in'];
    case 'signed_in':
      return s.expires_at && daysUntil(s.expires_at, now) < RENEW_WARNING_DAYS ? ['warm', 'Expires soon'] : ['hot', 'Signed in'];
    case 'expired':
      return ['cold', 'Expired'];
    case 'error':
      return ['cold', 'Needs sign-in'];
    default:
      return ['', 'Not signed in'];
  }
}

/** "Signed in by Asha Rao on 17 Oct 2026 · expires on 17 Oct 2027" — the stored sign-in in one line, never the token. */
export function claudeLoginLine(s: Pick<ClaudeLoginStatus, 'set_by' | 'set_at' | 'expires_at'>, now = Date.now()): string {
  if (!s.set_at) return '';
  const who = s.set_by ? ` by ${s.set_by.name}` : '';
  const exp = !s.expires_at
    ? ' · no end date given by Claude'
    : new Date(s.expires_at).getTime() <= now
      ? ` · expired on ${fullDayLabel(s.expires_at)}`
      : ` · expires on ${fullDayLabel(s.expires_at)}`;
  return `Signed in${who} on ${fullDayLabel(s.set_at)}${exp}`;
}

// ---------------------------------------------------------------- display helpers

/** Usage rows carry the provider and purpose as plain strings (`stub`, `test` included). */
export const providerLabel = (p: string) => LLM_PROVIDER_LABEL[p as LlmProviderId] ?? (p === 'stub' ? 'Stub (staging)' : p);
export const purposeLabel = (p: string) =>
  p === 'test' ? 'Settings test' : p === 'default' ? 'Default' : (LLM_PURPOSE_LABEL[p as keyof typeof LLM_PURPOSE_LABEL] ?? p);

/** The models a purpose may pick for a provider: its allow-list, and `default` for the Claude CLI (contract §4). */
export function modelsFor(s: Pick<LlmSettingsResponse, 'known_models'>, provider: LlmProviderId): string[] {
  const known = s.known_models[provider] ?? [];
  return provider === 'claude_cli' && !known.includes('default') ? ['default', ...known] : known;
}

export const usd = (n: number) => `$${n < 1 && n > 0 ? n.toFixed(4) : n.toFixed(2)}`;
export const int = (n: number) => n.toLocaleString('en-IN');

/** "Voice notes and Email tone" — a plain-words list. */
const andList = (xs: string[]) => (xs.length < 2 ? (xs[0] ?? '') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);

/**
 * Why a key could not be removed (DELETE 409), named the way the card's "Used by" line names them — the server's
 * message carries raw purpose ids. `inUseBy` is the provider's `in_use_by` (purposes, and `default`).
 */
export function keyInUseText(label: string, inUseBy: readonly string[]): string {
  const purposes = inUseBy.filter((x) => x !== 'default').map(purposeLabel);
  const isDefault = inUseBy.includes('default');
  const what = purposes.length
    ? `${label} still runs ${andList(purposes)}${isDefault ? ', and is the default' : ''}`
    : isDefault
      ? `${label} is still the default provider`
      : `${label} is still in use`;
  return `The key was not removed: ${what}. Move ${purposes.length + (isDefault ? 1 : 0) > 1 ? 'those' : 'it'} to another provider first.`;
}
